import { useState } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, ArrowUpRight, ArrowDownRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money, formatDateTime } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const CLIENT_KINDS = ["extra_service", "extra_document", "waiting_time", "route_change", "urgency", "other"];
const PARTNER_KINDS = ["delay", "mistake", "missing_document", "service_issue", "other"];

export function AdjustmentsPanel({ order, onUpdated }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("client");
  const [form, setForm] = useState({ kind: "extra_service", label: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);
  const clientExtras = order.client_extras || [];
  const partnerDeductions = order.partner_deductions || [];

  const openFor = (tg) => {
    setTarget(tg);
    setForm({ kind: (tg === "client" ? CLIENT_KINDS : PARTNER_KINDS)[0], label: "", amount: "", note: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.label || !form.amount || Number(form.amount) <= 0) { toast.error(t("adj.needFields")); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/charges`, {
        target, kind: form.kind, label: form.label, amount: Number(form.amount), note: form.note || null,
      });
      onUpdated?.(data); setOpen(false); toast.success(t("adj.added"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const kinds = target === "client" ? CLIENT_KINDS : PARTNER_KINDS;
  const kindLabel = (v) => t(`adj.${target === "client" ? "ck" : "pk"}.${v}`);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="adjustments-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><SlidersHorizontal size={18} /> {t("adj.title")}</h3>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => openFor("client")} data-testid="add-client-charge-btn">
            <ArrowUpRight size={14} className="mr-1 text-emerald-600" /> {t("adj.clientCharge")}
          </Button>
          <Button size="sm" variant="outline" onClick={() => openFor("partner")} data-testid="add-partner-deduction-btn">
            <ArrowDownRight size={14} className="mr-1 text-rose-600" /> {t("adj.partnerDeduction")}
          </Button>
        </div>
      </div>

      {clientExtras.length === 0 && partnerDeductions.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="adjustments-empty">{t("adj.empty")}</p>
      ) : (
        <div className="space-y-2" data-testid="adjustments-history">
          {clientExtras.map((c) => (
            <div key={c.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2" data-testid="adjustment-client-row">
              <div>
                <span className="font-medium text-slate-800">{c.label}</span>
                <span className="text-xs text-slate-400 ml-2 capitalize">{(c.kind || "").replace(/_/g, " ")} · {formatDateTime(c.at)}</span>
              </div>
              <span className="font-semibold text-emerald-700">+{money(c.amount, order.currency)}</span>
            </div>
          ))}
          {partnerDeductions.map((c) => (
            <div key={c.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2" data-testid="adjustment-partner-row">
              <div>
                <span className="font-medium text-slate-800">{c.label}</span>
                <span className="text-xs text-slate-400 ml-2 capitalize">{(c.kind || "").replace(/_/g, " ")} · {formatDateTime(c.at)}</span>
              </div>
              <span className="font-semibold text-rose-600">-{money(c.amount, order.currency)}</span>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="adjustment-dialog">
          <DialogHeader><DialogTitle className="font-display">
            {target === "client" ? t("adj.clientTitle") : t("adj.partnerTitle")}
          </DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>{t("adj.type")}</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="adjustment-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{kinds.map((k) => <SelectItem key={k} value={k}>{kindLabel(k)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>{t("adj.desc")}</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="adjustment-label" /></div>
            <div className="space-y-1.5"><Label>{t("adj.amount")} ({order.currency || "EUR"})</Label>
              <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="adjustment-amount" /></div>
            <div className="space-y-1.5"><Label>{t("adj.noteOpt")}</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="adjustment-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="adjustment-save-btn">{saving ? "..." : t("adj.add")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
